import { useEffect, useState } from 'react';
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import AddIcon from '@mui/icons-material/Add';
import { CreditCard, LocalActivity, Person } from '@mui/icons-material';
import { FaUser } from 'react-icons/fa';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import StatsCard, { StatsCardProps } from './StatsCard';
import WSDataGrid, { IColumns } from '../../../Components/DataGrid/WSDataGrid';
import { IRows } from '../../../Components/Filter/WSFilterComponent';
import { useSnackbar } from '../../../Components/Snackbar/SnackBarContext';
import { callAPI, Method } from '../../../commonFunctions/CommonFunctions';
import { resetDataComponent } from '../../../StateManagement/actions';
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import ConfirmationPopup from '../../../Components/Confirmation/Confirmation';
import OtpModal from '../../../Components/Otp/OtpModal';
import http from '../../../commonFunctions/httpClient';
import WSLoadingOverlay from '../../../Components/Loader/WSLoadingOverlay';
import WSButton from '../../../Components/Button/WSButton';
import DigitalCard from './DigitalCardDialog';

export type CardType = 'Green' | 'Silver' | 'Gold';

export interface Branches {
    _id: string,
    name: string,
    code: string,
    location: string,
}

const DigitalCardManagement = () => {
    const { watch, handleSubmit, setValue, reset, control, } = useForm();
    const { showMessage } = useSnackbar();
    const dispatch = useDispatch();

    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [saving, setSaving] = useState(false);
    const [selectedIdDlt, setSelectedIdDlt] = useState<string | null>(null);
    const [dltPopupOpen, setDltPopupOpen] = useState(false);

    const [cardType, setCardType] = useState<CardType>('Green');
    const [editCard, setEditCard] = useState<any | null>(null)
    const [openDialog, setOpenDialog] = useState(false);

    const [otpModalOpen, setOtpModalOpen] = useState(false);
    const [customerId, setCustomerId] = useState("");
    const [mobileNumber, setMobileNumber] = useState("");

    const [isLoading, setIsLoading] = useState(false);
    const [isResending, setIsResending] = useState(false);

    const [branches, setBranches] = useState<Branches[]>([]);

    const [totalCards, setTotalCards] = useState<StatsCardProps>({
        value: 0,
        trend: 0,
        label: "",
        subtitle: '+3 this month',
        icon: 'CreditCard',
        color: 'primary'
    });

    const [activeCards, setActiveCards] = useState<StatsCardProps>({
        value: 0,
        trend: 0,
        label: "",
        subtitle: '100% active rate',
        icon: 'Person',
        color: 'success'
    });

    const [totalPoints, setTotalPoints] = useState<StatsCardProps>({
        value: 0,
        trend: 0,
        label: "",
        subtitle: '18 points today',
        icon: 'LocalActivity',
        color: 'warning'
    });

    const [totalCustomers, settotalCustomers] = useState<StatsCardProps>({
        value: 0,
        trend: 0,
        label: "",
        subtitle: '18 points today',
        icon: 'LocalActivity',
        color: 'warning'
    });

    const source = {
        url: "customers/search",
        page: 1,
        pageSize: 10,
        filter: [],
        sort: [],
    };

    async function getData() {
        try {
            setLoading(true)
            const response: any = await callAPI(`dashboards/stats`, Method.GET);

            if (response && response?.data) {
                setTotalCards(response.data.totalCards)
                setActiveCards(response.data.activeCards)
                setTotalPoints(response.data.totalPoints)
                settotalCustomers(response.data.totalCustomers);
            }
        } catch (error) {
            console.error("failed to retrieve data", error);
        } finally {
            setLoading(false)
        }
    }

    async function getBranches() {
        try {
            setLoading(true)
            const response: any = await callAPI(`branches/getAll`, Method.GET);
            console.log('response is', response);

            if (response && response?.data) {
                setBranches(response.data);
            }
        } catch (error) {
            console.error("failed to retrieve data", error);
        } finally {
            setLoading(false)
        }
    }


    useEffect(() => {
        getData();
        getBranches();
    }, []);

    const handleSendOtp = async (number: string, id: string) => {
        try {
            setIsResending(true);
            setSending(true);
            const response = await http.sendMessage("send-message", {
                to: number,
                templateName: "otp_verification",
                type: "otp",
            });

            if (response) {
                setCustomerId(id);
                setMobileNumber(number);
                setOtpModalOpen(true);
                showMessage("OTP sent successfully!", "success");
            }
        } catch (error: any) {
            console.error("Error sending OTP:", error);
            showMessage(error.response?.data?.message || "Failed to send OTP", 'error');
        } finally {
            setIsResending(false);
            setSending(false);
        }
    };

    const handleVerifyOtp = async (otp: string) => {
        try {
            setIsLoading(true);

            const response = await http.sendMessage("verify-otp", {
                mobileNumber: mobileNumber,
                otp: otp
            });

            if (response) {
                const mpinRes = await callAPI(`customers/set-mpin/${customerId}`, Method.POST, {
                    mobileNumber: mobileNumber,
                });

                if (mpinRes.success) {
                    showMessage(mpinRes.message, 'success');
                    setOtpModalOpen(false);
                    dispatch(resetDataComponent("digitalCardId"));
                } else {
                    showMessage("Customer verification failed", 'error');
                }
            } else {
                showMessage("OTP verification failed", 'error');
            }
        } catch (error: any) {
            console.error("Error verifying OTP:", error);
            showMessage(error.response?.data?.message || "OTP verification failed", 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendOtp = async () => {
        if (mobileNumber && customerId) {
            await handleSendOtp(mobileNumber, customerId);
        }
    };

    const handleCloseOtpModal = () => {
        setOtpModalOpen(false)
    }

    const handleGenerateCard = async (data: any) => {
        console.log('data is', data);
        try {
            setSaving(true);

            let request = {
                ...data,
                cardType,
                branches: data.branches.map((b: { branchId: string, openingPoints: number }) => ({
                    branchId: b.branchId,
                    openingPoints: Number(b.openingPoints) || 0
                }))
            };

            // Normalize mobileNumber (converting to correct)
            if (data.mobileNumber) {
                const phone = parsePhoneNumberFromString(data.mobileNumber, 'IN');
                if (phone && phone.isValid()) {
                    request.mobileNumber = phone.number; // E.164 format
                } else {
                    showMessage("Invalid mobile number format", "error");
                    return;
                }
            }

            if (editCard) {
                const response: any = await callAPI(`customers/update/${editCard.customer._id}`, Method.PUT, request);
                if (response) {
                    if (response.data.verificationReset) {
                        showMessage(
                            "Customer updated successfully. Mobile number changed - customer needs to verify again to set MPIN.",
                            'warning'
                        );
                    } else {
                        showMessage(response.message, 'success');
                    }
                    setOpenDialog(false);
                    dispatch(resetDataComponent("digitalCardId"));
                }
            } else {
                const response: any = await callAPI("customers/onboard", Method.POST, request);
                if (response) {
                    showMessage(response?.message, response.success === true ? "success" : "error");
                    setOpenDialog(false);
                    dispatch(resetDataComponent("digitalCardId"));
                }
            }
        } catch (e: any) {
            showMessage(e?.response?.data?.message || "something went wrong", 'error');
        } finally {
            setSaving(false)
            setCardType("Green");
            reset({
                cardNumber: '',
                customerName: '',
                mobileNumber: '',
                emailAddress: '',
                addressOne: '',
                addressTwo: '',
                city: '',
                state: '',
                postalCode: '',
                anniversary: '',
                openingPoints: 0
            });
        }
    };

    const handleOpen = () => {
        setOpenDialog(true);
    }

    const handleClose = () => {
        setOpenDialog(false);
        setEditCard(null);
        setCardType("Green");
        reset({
            cardNumber: '',
            customerName: '',
            mobileNumber: '',
            emailAddress: '',
            addressOne: '',
            addressTwo: '',
            city: '',
            state: '',
            postalCode: '',
            anniversary: '',
            openingPoints: 0
        });
    }

    const handleAction = (action: string, row: any) => {
        if (action === "Edit") {
            console.log('row data when edit', row);
            setEditCard(row);
            setOpenDialog(true);
        } else if (action === "Delete") {
            setSelectedIdDlt(row.customer._id);
            setDltPopupOpen(true);
        } else if (action === 'Verify') {
            if (row.customer.mobileNumber) {
                handleSendOtp(row.customer.mobileNumber, row.customer._id);
            }
        }
    };

    const handleDelete = () => {
        dispatch(resetDataComponent("digitalCardId"));
        getData();
        showMessage("Customer deleted successfully!", "success");
    };


    let dropdownOption = [{ label: "Verified", value: "true" }, { label: "UnVerified", value: "false" }]

    const columns: IColumns[] = [
        {
            header: "Card Number",
            fieldName: "cardNumber",
            type: "default",
        },

        {
            header: "Card Type",
            fieldName: "cardType",
            tooltip: true,
            type: "default",
        },

        {
            header: "Customer Name",
            fieldName: "customer.customerName",
            tooltip: true,
            type: "default",
        },
        {
            header: "Mobile Number",
            fieldName: "customer.mobileNumber",
            tooltip: true,
            type: "default",
        },

        {
            header: "Last Updated",
            fieldName: "updatedAt",
            type: "date",
        },

        {
            header: "Verified",
            fieldName: "customer.isVerified",
            type: "status",
        },

        {
            header: "Actions",
            fieldName: "actions",
            type: "action",
            actions: ["Edit", "Delete", "Verify"],
            actionCallback: (action, row) => handleAction(action, row),
        },
    ];

    const filterRows: IRows[] = [
        {
            label: "Customer Name",
            fieldName: "customerName",
            type: "text",
            operator: "regex"
        },

        {
            label: "Card Number",
            fieldName: "cardNumber",
            type: "text",
        },

        { type: "dropdown", fieldName: "isVerified", label: "Status", options: dropdownOption },
    ];

    return (
        <Box sx={{ p: 3 }}>
            {/* Header Section */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="h5" fontWeight="600">
                    Digital Card Management
                </Typography>
                <WSButton id='issueNewCard' label='Issue New Card' variant='contained' startIcon={<AddIcon />} size='small' sx={{ px: 1, py: 1 }} onClick={handleOpen} />
            </Box>

            {/* Stats Cards */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
                <StatsCard
                    title="Total Cards"
                    value={totalCards.value}
                    trend={totalCards.trend}
                    label={totalCards.label}
                    subtitle={totalCards.subtitle}
                    icon={<CreditCard sx={{ fontSize: 28 }} />}
                    color="primary"
                    loading={loading}
                />

                <StatsCard
                    title="Total Active Cards"
                    value={activeCards.value}
                    trend={activeCards.trend}
                    label={activeCards.label}
                    subtitle={activeCards.subtitle}
                    icon={<Person sx={{ fontSize: 28 }} />}
                    color="info"
                    loading={loading}
                />

                <StatsCard
                    title="Total Customers"
                    value={totalCustomers.value}
                    trend={totalCustomers.trend}
                    label={totalCustomers.label}
                    subtitle={totalCustomers.subtitle}
                    icon={<FaUser />}
                    color="success"
                    loading={loading}
                />

                <StatsCard
                    title="Total Points Earned"
                    value={totalPoints.value}
                    trend={totalPoints.trend}
                    label={totalPoints.label}
                    subtitle={totalPoints.subtitle}
                    icon={<LocalActivity sx={{ fontSize: 28 }} />}
                    color="warning"
                    loading={loading}
                />
            </Grid>

            <WSDataGrid id='digitalCardId'
                columns={columns}
                source={source}
                searchBox={{ fieldName: "mobileNumber" }}
                filterFields={filterRows} />

            <DigitalCard
                onClose={handleClose}
                handleGenerateCard={handleSubmit(handleGenerateCard)}
                setValue={setValue}
                reset={reset}
                watch={watch} isLoading={saving}
                open={openDialog} cardType={cardType}
                branches={branches}
                setCardType={setCardType} control={control} editCard={editCard} />

            <OtpModal
                isResending={isResending} open={otpModalOpen}
                numberOfColunms={6} isLoading={isLoading} mobileNumber={mobileNumber}
                onClose={handleCloseOtpModal} onResendOtp={handleResendOtp} onOtpSubmit={handleVerifyOtp} />

            {selectedIdDlt !== null && (
                <ConfirmationPopup
                    apiUrl={"customers/delete"}
                    id={selectedIdDlt}
                    confirmMessage="Are you sure you want to delete this item?"
                    open={dltPopupOpen}
                    onClose={() => setDltPopupOpen(false)}
                    onConfirm={handleDelete}
                />
            )}

            <WSLoadingOverlay loading={sending} />
        </Box>
    );
};

export default DigitalCardManagement;